import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bugsAPI, changeRequestsAPI, projectsAPI, usersAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { PageHeader, EmptyState, SearchInput } from '../components/layout/UI';
import BugFormFields, { bugToForm, bugFormValid, emptyBugForm } from '../components/BugFormFields';
import ChangeRequestFormFields, { crToForm, crFormValid, emptyCRForm } from '../components/ChangeRequestFormFields';
import { isAdminRole } from '../utils/rolesConfig';
import { format } from 'date-fns';

import { useToast, apiError, useConfirm } from '../components/Toast';
import { Bug, ChevronDown, ChevronUp, ExternalLink, GitPullRequest, Plus, SquarePen, Trash2, X } from 'lucide-react';

const PlusIc = () => <Plus size={13} />;
const ChevIc = ({ open }) => open ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
const XIcon = () => <X size={16} />;

const SEV = {
  Critical:{ bg:'#fef2f2', border:'#ef4444', text:'#b91c1c', dot:'#ef4444' },
  Major:   { bg:'#fff7ed', border:'#f97316', text:'#c2410c', dot:'#f97316' },
  Minor:   { bg:'#fffbeb', border:'#f59e0b', text:'#b45309', dot:'#f59e0b' },
  Low:     { bg:'#f0fdf4', border:'#22c55e', text:'#15803d', dot:'#22c55e' },
};
const PRI = {
  Critical:{ bg:'#fef2f2', border:'#ef4444', text:'#b91c1c' },
  High:    { bg:'#fff7ed', border:'#f97316', text:'#c2410c' },
  Medium:  { bg:'#fffbeb', border:'#f59e0b', text:'#b45309' },
  Low:     { bg:'#f0fdf4', border:'#22c55e', text:'#15803d' },
};

const STATUS_COLOR = {
  Open:          '#ef4444', 'In Progress':'#f97316', Fixed:'#10b981',
  Verified:      '#3b82f6', Closed:'#94a3b8',
  Pending:       '#f59e0b', 'Under Review':'#8b5cf6', Approved:'#10b981',
  Rejected:      '#ef4444', Implemented:'#3b82f6',
};

// Must match bugs_status_check / change_requests_approval_status_check.
const BUG_STATUSES = ['Open','In Progress','Fixed in Dev','In Testing','Fixed',
                    'Deployed to UAT','Deployed to Production','Resolved',
                    'Verified','Closed'];
const CR_STATUSES  = ['Pending','Under Review','Requirements Shared','BRD Signed Off',
                       'Approved','In Development','Testing','Deployed',
                       'Implemented','Rejected'];

const EditIc = () => <SquarePen size={13} />;
const TrashIc = () => <Trash2 size={13} />;

// Shared column template. Header and rows both use this, so they cannot drift.
// Type | Title | Module/Dept | Reporter/Requestor | Severity/Priority | Status | Actions
const GRID = '52px minmax(0,1fr) 140px 150px 96px 132px 104px';
const ROW_MIN = 960; // below this the group scrolls sideways rather than squashing

// -- Bug row (Jira-style) -------------------------------------------------------
function BugRow({ bug, onStatusChange, onEdit, onDelete, canDelete }) {
  const sc = SEV[bug.severity] || SEV.Minor;
  const typeLabel = {Bug:'Bug','Request for Fix':'Fix',Support:'Help','New Feature':'New',REOPEN:'Reopen'}[bug.issue_type||'Bug']||'Bug';
  const statusColor = STATUS_COLOR[bug.status] || '#94a3b8';
  return (
    <div className="grid items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-[var(--surface-alt)] transition-colors group"
      style={{ borderColor:'var(--border)', gridTemplateColumns:GRID, minWidth:ROW_MIN }}>
      {/* Type */}
      <span className="text-xs font-bold px-2 py-0.5 rounded text-center"
        style={{ background:`${sc.border}18`, color:sc.text }}>{typeLabel}</span>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <Link to={`/bugs/${bug.id}`}
          className="text-sm font-semibold hover:text-brand-500 transition-colors block truncate"
          style={{ color:'var(--text)' }}>
          {bug.ticket_number && <span className="text-xs font-mono mr-2 opacity-50">{bug.ticket_number}</span>}
          {bug.title}
        </Link>
      </div>

      {/* Module */}
      <span className="text-xs truncate" style={{ color:'var(--text-secondary)' }}
        title={bug.module ? `${bug.module}${bug.section ? ' / ' + bug.section : ''}` : ''}>
        {bug.module ? `${bug.module}${bug.section ? ' / ' + bug.section : ''}` : '—'}
      </span>

      {/* Reporter */}
      <span className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>
        {bug.created_by_name || '—'}
        {bug.created_at && <span className="block opacity-60">{format(new Date(bug.created_at),'MMM d, yyyy')}</span>}
      </span>

      {/* Severity */}
      <span>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ background:sc.bg, color:sc.text, border:`1px solid ${sc.border}` }}>{bug.severity}</span>
      </span>

      {/* Status */}
      <select
        className="text-xs py-1 px-2 rounded-lg border font-medium w-full"
        style={{ borderColor:statusColor+'60', color:statusColor, background:`${statusColor}10` }}
        value={bug.status}
        onClick={e => e.stopPropagation()}
        onChange={e => onStatusChange(bug.id, e.target.value, 'bug')}>
        {BUG_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      {/* Actions — always visible, not hover-only, so they can be found.
          Delete shows only for the creator (or Admin), matching the API. */}
      <div className="flex items-center justify-end gap-1">
        <button onClick={e => { e.stopPropagation(); onEdit(bug); }} title="Edit"
          className="p-1.5 rounded hover:bg-[var(--surface)]" style={{ color:'var(--text-secondary)' }}>
          <EditIc/>
        </button>
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(bug); }} title="Delete"
            className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
            <TrashIc/>
          </button>
        )}
        <Link to={`/bugs/${bug.id}`} title="Open detail"
          className="p-1.5 rounded" style={{ color:'var(--text-secondary)' }}>
          <ExternalLink size={12}/>
        </Link>
      </div>
    </div>
  );
}

// -- CR row (Jira-style) --------------------------------------------------------
function CRRow({ cr, onStatusChange, onEdit, onDelete, canDelete }) {
  const level  = cr.impact_level || cr.priority || 'Medium';
  const pc     = PRI[level] || PRI.Medium;
  const status = cr.approval_status || 'Pending';
  const statusColor = STATUS_COLOR[status] || '#94a3b8';
  const total  = [cr.estimated_hours_workflow,cr.estimated_hours_offworkflow,cr.estimated_hours_templates,cr.estimated_hours_testing]
    .filter(Boolean).reduce((s,v)=>s+parseFloat(v),0);
  return (
    <div className="grid items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-[var(--surface-alt)] transition-colors group"
      style={{ borderColor:'var(--border)', gridTemplateColumns:GRID, minWidth:ROW_MIN }}>
      {/* Type */}
      <span className="text-xs font-bold px-2 py-0.5 rounded text-center"
        style={{ background:'rgba(139,92,246,.1)', color:'#7c3aed' }}>CR</span>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <Link to={`/change-requests/${cr.id}`}
          className="text-sm font-semibold hover:text-brand-500 transition-colors block truncate"
          style={{ color:'var(--text)' }}>
          {cr.ticket_number && <span className="text-xs font-mono mr-2 opacity-50">{cr.ticket_number}</span>}
          {cr.title}
        </Link>
        {total > 0 && (
          <span className="text-xs" style={{ color:'var(--text-secondary)' }}>{total}h est.</span>
        )}
      </div>

      {/* Department — the CR equivalent of a bug's module */}
      <span className="text-xs truncate" style={{ color:'var(--text-secondary)' }}
        title={cr.department || ''}>{cr.department || '—'}</span>

      {/* Requestor */}
      <span className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>
        {cr.authorized_requestor || cr.requested_by_name || '—'}
        {cr.created_at && <span className="block opacity-60">{format(new Date(cr.created_at),'MMM d, yyyy')}</span>}
      </span>

      {/* Priority */}
      <span>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ background:pc.bg, color:pc.text, border:`1px solid ${pc.border}` }}>{level}</span>
      </span>

      {/* Status */}
      <select
        className="text-xs py-1 px-2 rounded-lg border font-medium w-full"
        style={{ borderColor:statusColor+'60', color:statusColor, background:`${statusColor}10` }}
        value={status}
        onClick={e => e.stopPropagation()}
        onChange={e => onStatusChange(cr.id, e.target.value, 'cr')}>
        {CR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      {/* Actions — always visible, not hover-only, so they can be found.
          Delete shows only for the creator (or Admin), matching the API. */}
      <div className="flex items-center justify-end gap-1">
        <button onClick={e => { e.stopPropagation(); onEdit(cr); }} title="Edit"
          className="p-1.5 rounded hover:bg-[var(--surface)]" style={{ color:'var(--text-secondary)' }}>
          <EditIc/>
        </button>
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(cr); }} title="Delete"
            className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
            <TrashIc/>
          </button>
        )}
        <Link to={`/change-requests/${cr.id}`} title="Open detail"
          className="p-1.5 rounded" style={{ color:'var(--text-secondary)' }}>
          <ExternalLink size={12}/>
        </Link>
      </div>
    </div>
  );
}

// -- Project group accordion ---------------------------------------------------
function ProjectGroup({ group, tab, onStatusChange, expanded, onToggle, onEdit, onDelete, canDelete }) {
  const items   = tab === 'bugs' ? (group.bugs||[]) : (group.change_requests||[]);
  if (!items.length) return null;
  const open    = items.filter(i => !['Closed','Verified','Implemented','Rejected'].includes(i.status||i.approval_status)).length;
  const critical = tab === 'bugs' ? items.filter(i => i.severity === 'Critical').length : 0;
  return (
    <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface-alt)] transition-colors"
        style={{ background:'var(--surface)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ background:'#2E539F' }}>
          {(group.project_name||'?')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>{group.project_name||'Unassigned'}</p>
          {group.client_name && <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Client: {group.client_name}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {critical > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background:'#fef2f2', color:'#b91c1c' }}>
              {critical} critical
            </span>
          )}
          <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>
            {open} open / {items.length} total
          </span>
          <ChevIc open={expanded}/>
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto" style={{ background:'var(--surface)' }}>
          {/* Column headers */}
          <div className="grid items-center gap-3 px-4 py-2 border-t border-b"
            style={{ borderColor:'var(--border)', background:'var(--surface-alt)',
                     gridTemplateColumns:GRID, minWidth:ROW_MIN }}>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Type</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Title</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>
              {tab === 'bugs' ? 'Module' : 'Department'}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>
              {tab === 'bugs' ? 'Reporter' : 'Requestor'}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>
              {tab === 'bugs' ? 'Severity' : 'Priority'}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Status</span>
            <span className="text-xs font-bold uppercase tracking-wider text-right" style={{ color:'var(--text-secondary)' }}>Actions</span>
          </div>
          {tab === 'bugs'
            ? items.map(b => <BugRow key={b.id} bug={b} onStatusChange={onStatusChange}
                onEdit={onEdit} onDelete={onDelete} canDelete={canDelete(b.created_by)}/>)
            : items.map(cr => <CRRow key={cr.id} cr={cr} onStatusChange={onStatusChange}
                onEdit={onEdit} onDelete={onDelete} canDelete={canDelete(cr.requested_by)}/>)
          }
        </div>
      )}
    </div>
  );
}

// -- Quick modal ---------------------------------------------------------------
function QuickModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
      <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-lg z-10 max-h-[90vh] flex flex-col"
        style={{ border:'1px solid var(--border)' }}>
        <div className="px-6 py-4 border-b flex-shrink-0 flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
          <h3 className="font-bold text-base" style={{ color:'var(--text)' }}>{title}</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--surface-alt)]"
            style={{ color:'var(--text-secondary)' }}><XIcon/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// -- Main page -----------------------------------------------------------------
export default function DevHubPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [tab,      setTab]      = useState('bugs');
  const [error, setError] = useState('');
  const [bugData,  setBugData]  = useState([]);
  const [crData,   setCrData]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [statusF,  setStatusF]  = useState('');
  const [severityF, setSeverityF] = useState('');
  // Group by whatever answers the question you arrived with. Project is
  // 'whose work is this'; assignee is 'what should I pick up'; severity is
  // 'what is on fire'. The page only ever offered the first.
  const [groupBy, setGroupBy] = useState('project');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [assigneeF, setAssigneeF] = useState('');
  const [expanded, setExpanded] = useState({});

  const [bugModal, setBugModal] = useState(false);
  const [bugForm,  setBugForm]  = useState({});
  const [editingId, setEditingId] = useState(null);
  const [users,    setUsers]    = useState([]);

  // Feeds the Assign To dropdowns on both the bug and CR forms.
  useEffect(() => {
    usersAPI.getAll().then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);
  const [bugSaving,setBugSaving]= useState(false);
  const [crModal,  setCrModal]  = useState(false);
  const [crForm,   setCrForm]   = useState({});
  const [crSaving, setCrSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [br, crr] = await Promise.all([
        bugsAPI.byProject({ include_closed: includeClosed ? '1' : undefined }),
        changeRequestsAPI.byProject({ include_closed: includeClosed ? '1' : undefined }),
      ]);
      setBugData(br.data||[]); setCrData(crr.data||[]);
      // Auto-expand first group
      const firstBugKey = (br.data||[])[0]?.project_id;
      if (firstBugKey) setExpanded(p => Object.keys(p).length === 0 ? { [firstBugKey]:true } : p);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [includeClosed]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { projectsAPI.getAll({ limit: 0 }).then(r => setProjects(r.data||[])).catch(e => toast.error(apiError(e, 'Could not load this page.'))); }, []);

  // Delete is the creator's alone, plus Admin level — the same rule the API
  // enforces, so a button never appears that would come back 403.
  const canDelete = (creatorId) => creatorId === user?.id || isAdminRole(user?.role);

  const openEditFromRow = (row) => {
    if (tab === 'bugs') { setBugForm(bugToForm(row)); setEditingId(row.id); setBugModal(true); }
    else                { setCrForm(crToForm(row)); setEditingId(row.id); setCrModal(true); }
  };

  const handleDeleteRow = async (row) => {
    const what = tab === 'bugs' ? 'bug' : 'change request';
    if (!await confirm(`Delete this ${what}? This cannot be undone.`)) return;
    try {
      if (tab === 'bugs') await bugsAPI.delete(row.id);
      else                await changeRequestsAPI.delete(row.id);
      toast.success('Change request deleted');
      load();
    } catch(e) { toast.error(apiError(e, `Failed to delete the ${what}.`)); }
  };

  const openBugModal = () => {
    setEditingId(null);
    setBugForm({ ...emptyBugForm(), assigned_to: user?.id || '' });
    setBugModal(true);
  };
  const openCRModal = () => {
    setEditingId(null);
    setCrForm({ ...emptyCRForm(), assigned_to: user?.id || '' });
    setCrModal(true);
  };

  const submitBug = async () => {
    if (!bugForm.project_id || !bugForm.title) return;
    setBugSaving(true);
    try {
      if (editingId) await bugsAPI.update(editingId, bugForm);
      else           await bugsAPI.create(bugForm);
      toast.success('Bug saved');
      setBugModal(false); setEditingId(null); load();
    }
    catch(e) { toast.error(apiError(e, 'Error submitting bug')); }
    finally { setBugSaving(false); }
  };
  const submitCR = async () => {
    if (!crForm.project_id || !crForm.title || !crForm.description) return;
    setCrSaving(true);
    try {
      if (editingId) await changeRequestsAPI.update(editingId, crForm);
      else           await changeRequestsAPI.create(crForm);
      toast.success('Change request saved');
      setCrModal(false); setEditingId(null); load();
    }
    catch(e) { toast.error(apiError(e, 'Error submitting CR')); }
    finally { setCrSaving(false); }
  };

  const handleStatusChange = async (id, status, type) => {
    try {
      if (type === 'bug') await bugsAPI.update(id, { status });
      else await changeRequestsAPI.update(id, { approval_status: status });
      toast.success('Change request saved');
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to update status.')); }
  };

  const toggleGroup = key => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const currentData = tab === 'bugs' ? bugData : crData;
  /**
   * Filter first, then regroup. Grouping by assignee or severity means the
   * project grouping the API returned is no longer the one being shown, so
   * the rows are flattened and rebuilt around whichever key is chosen.
   */
  const key = tab === 'bugs' ? 'bugs' : 'change_requests';

  const matches = (i) => {
    const title  = i.title?.toLowerCase() || '';
    const ticket = (i.ticket_number || '').toLowerCase();
    const q = search.toLowerCase();
    if (search && !title.includes(q) && !ticket.includes(q)) return false;
    if (statusF && (i.status || i.approval_status) !== statusF) return false;
    if (severityF && (tab === 'bugs' ? i.severity : i.impact_level) !== severityF) return false;
    if (assigneeF && i.assigned_to !== assigneeF) return false;
    return true;
  };

  const flat = currentData.flatMap(g =>
    (g[key] || []).filter(matches).map(i => ({ ...i, _project: g.project_name || g.name || 'No project' })));

  const GROUP_OF = {
    project:  i => i._project,
    assignee: i => i.assigned_to_name || 'Unassigned',
    severity: i => (tab === 'bugs' ? i.severity : i.impact_level) || 'None set',
  };

  const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low', 'None set'];

  const filteredData = Object.entries(
    flat.reduce((acc, i) => {
      const g = GROUP_OF[groupBy](i);
      (acc[g] = acc[g] || []).push(i);
      return acc;
    }, {})
  )
    .sort(([a], [b]) => groupBy === 'severity'
      // Severity sorts by how bad it is, not alphabetically — Critical above
      // High above Medium is the only order that reads correctly.
      ? SEVERITY_ORDER.indexOf(a) - SEVERITY_ORDER.indexOf(b)
      : a.localeCompare(b))
    .map(([name, items]) => ({
      project_id: name, project_name: name, name,
      [key]: items,
      ...(tab === 'bugs' ? { change_requests: [] } : { bugs: [] }),
    }));

  /**
   * Counted across what the filters show, not across everything. Filtering to
   * one project and still reading the system-wide total made the numbers
   * useless for the thing you had just narrowed to.
   */
  const shown     = flat;
  const totalBugs = tab === 'bugs' ? shown.length : bugData.reduce((s,g)=>s+(g.bugs?.length||0),0);
  const totalCRs  = tab === 'bugs' ? crData.reduce((s,g)=>s+(g.change_requests?.length||0),0) : shown.length;
  const openBugs  = tab === 'bugs' ? shown.filter(b=>!['Closed','Verified'].includes(b.status)).length : 0;
  const openCRs   = tab === 'bugs' ? 0 : shown.filter(c=>!['Implemented','Rejected'].includes(c.approval_status)).length;
  const critBugs  = tab === 'bugs' ? shown.filter(b=>b.severity==='Critical'&&b.status!=='Closed').length : 0;

  const currentStatuses = tab === 'bugs' ? BUG_STATUSES : CR_STATUSES;

  return (
    <div className="space-y-5">
      <PageHeader title="Dev Hub" subtitle="Bug reports and change requests grouped by project">
        <div className="flex gap-2">
          <button onClick={openBugModal} className="btn-secondary text-sm flex items-center gap-1.5">
            <Bug size={14}/> Report Bug
          </button>
          <button onClick={openCRModal} className="btn-primary text-sm flex items-center gap-1.5">
            <PlusIc/> Submit CR
          </button>
          <Link to="/bugs" className="btn-secondary flex items-center gap-1.5 text-sm">
            <Bug size={14}/> Bug Tracker
          </Link>
          <Link to="/change-requests" className="btn-secondary flex items-center gap-1.5 text-sm">
            <PlusIc/> Change Requests
          </Link>
        </div>
      </PageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card px-4 py-3">
          <p className="text-2xl font-bold" style={{ color:critBugs>0?'#ef4444':'#2E539F' }}>{openBugs}</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Open Bugs</p>
          {critBugs>0&&<p className="text-xs font-semibold" style={{ color:'#ef4444' }}>{critBugs} critical</p>}
          {!critBugs&&<p className="text-xs" style={{ color:'var(--text-secondary)' }}>{totalBugs} total</p>}
        </div>
        <div className="card px-4 py-3">
          <p className="text-2xl font-bold" style={{ color:'#8b5cf6' }}>{openCRs}</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Open CRs</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{totalCRs} total</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-2xl font-bold" style={{ color:'#2E539F' }}>{bugData.filter(g=>g.bugs?.length).length}</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Projects w/ Bugs</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{bugData.length} total</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-2xl font-bold" style={{ color:'#5CC3BC' }}>{crData.filter(g=>g.change_requests?.length).length}</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Projects w/ CRs</p>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{crData.length} total</p>
        </div>
      </div>

      {/* Tab bar + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border overflow-hidden" style={{ borderColor:'var(--border)' }}>
          <button onClick={()=>setTab('bugs')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${tab==='bugs'?'bg-brand-500 text-white':''}`}
            style={tab!=='bugs'?{color:'var(--text-secondary)',background:'var(--surface)'}:{}}>
            <Bug size={14}/> Bug Reports
            {openBugs>0&&<span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background:tab==='bugs'?'rgba(255,255,255,.25)':'rgba(239,68,68,.12)', color:tab==='bugs'?'#fff':'#ef4444' }}>
              {openBugs}
            </span>}
          </button>
          <button onClick={()=>setTab('cr')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${tab==='cr'?'bg-brand-500 text-white':''}`}
            style={{...(tab!=='cr'?{color:'var(--text-secondary)',background:'var(--surface)'}:{}),borderColor:'var(--border)'}}>
            <GitPullRequest size={14}/> Change Requests
            {openCRs>0&&<span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background:tab==='cr'?'rgba(255,255,255,.25)':'rgba(139,92,246,.12)', color:tab==='cr'?'#fff':'#8b5cf6' }}>
              {openCRs}
            </span>}
          </button>
        </div>
        <SearchInput value={search} onChange={e=>setSearch(e.target.value)} placeholder={tab==='bugs'?'Search bugs...':'Search change requests...'}/>
        <select className="select text-sm w-40" value={statusF} onChange={e=>setStatusF(e.target.value)}>
          <option value="">All Statuses</option>
          {currentStatuses.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select className="select text-sm w-40" value={severityF} onChange={e=>setSeverityF(e.target.value)}>
          <option value="">{tab==='bugs' ? 'Any severity' : 'Any impact'}</option>
          {['Critical','High','Medium','Low'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select className="select text-sm w-44" value={assigneeF} onChange={e=>setAssigneeF(e.target.value)}>
          <option value="">Anyone</option>
          {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="select text-sm w-40" value={groupBy} onChange={e=>setGroupBy(e.target.value)}
          title="How the list is grouped">
          <option value="project">Group by project</option>
          <option value="assignee">Group by assignee</option>
          <option value="severity">{tab==='bugs' ? 'Group by severity' : 'Group by impact'}</option>
        </select>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap"
          style={{ color:'var(--text-secondary)' }}>
          <input type="checkbox" checked={includeClosed}
            onChange={e=>setIncludeClosed(e.target.checked)} />
          {tab==='bugs' ? 'Include closed' : 'Include implemented'}
        </label>
        <button onClick={load} className="text-xs px-3 py-2 rounded-lg border hover:bg-[var(--surface-alt)] transition-colors"
          style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : filteredData.length === 0 ? (
        <EmptyState icon={<Bug size={14}/>} title={search||statusF?'No results match your filters':tab==='bugs'?'No open bugs':'No open change requests'}
          message={search||statusF?'Try adjusting filters':'All clear! Nothing to track right now.'}/>
      ) : (
        <div className="space-y-3">
          {filteredData.map(group => (
            <ProjectGroup key={group.project_id||'unassigned'} group={group} tab={tab}
              onStatusChange={handleStatusChange}
              expanded={!!expanded[group.project_id||'unassigned']}
              onToggle={() => toggleGroup(group.project_id||'unassigned')}
              onEdit={openEditFromRow} onDelete={handleDeleteRow} canDelete={canDelete} />
          ))}
        </div>
      )}

      {/* Bug Report Modal */}
      <QuickModal open={bugModal} onClose={()=>setBugModal(false)} title={editingId ? "Edit Bug" : "Report a Bug"}>
        <div className="p-6 overflow-y-auto space-y-3">
          {/* Shared with BugsPage — add fields in BugFormFields.jsx, not here. */}
          <BugFormFields form={bugForm} setForm={setBugForm}
            projects={projects} users={users} me={user} />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor:'var(--border)' }}>
          <button onClick={()=>setBugModal(false)} className="btn-secondary">Cancel</button>
          <button onClick={submitBug} disabled={!bugFormValid(bugForm)||bugSaving} className="btn-primary flex items-center gap-2">
            {bugSaving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
            Submit Bug Report
          </button>
        </div>
      </QuickModal>

      {/* CR Modal */}
      <QuickModal open={crModal} onClose={()=>setCrModal(false)} title={editingId ? "Edit Change Request" : "Submit Change Request"}>
        <div className="p-6 overflow-y-auto space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Shared with ChangeRequestsPage — edit ChangeRequestFormFields.jsx. */}
            <div className="col-span-2">
              <ChangeRequestFormFields form={crForm} setForm={setCrForm}
                projects={projects} users={users} me={user} />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor:'var(--border)' }}>
          <button onClick={()=>setCrModal(false)} className="btn-secondary">Cancel</button>
          <button onClick={submitCR} disabled={!crFormValid(crForm)||crSaving} className="btn-primary flex items-center gap-2">
            {crSaving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
            Submit Change Request
          </button>
        </div>
      </QuickModal>
    </div>
  );
}
